import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { wishlistService, WishlistItemDto, WishlistResponseDto } from '../services/api/wishlist.service';
import { getApiErrorMessage } from '../utils/errorHandling';

type WishlistStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface WishlistState {
  items: WishlistItemDto[];
  count: number;
  status: WishlistStatus;
  error: string | null;
}

const initialState: WishlistState = {
  items: [],
  count: 0,
  status: 'idle',
  error: null,
};

export const fetchWishlist = createAsyncThunk('wishlist/fetch', async () => {
  return await wishlistService.getWishlist();
});

export const toggleWishlistItem = createAsyncThunk(
  'wishlist/toggle',
  async (payload: { productId: string; designId?: string }, { rejectWithValue }) => {
    try {
      return await wishlistService.toggleWishlistItem(payload);
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update wishlist'));
    }
  },
);

const signatureFor = (productId: string, designId?: string | null) => `${productId}:${designId ?? ''}`;

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    /** Clear server-backed wishlist (call on logout or when auth session ends). */
    resetWishlist: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action: { payload: WishlistResponseDto }) => {
        state.status = 'succeeded';
        state.items = action.payload.items || [];
        state.count = action.payload.count ?? state.items.length;
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.error?.message as string) || 'Failed to load wishlist';
      })
      .addCase(toggleWishlistItem.fulfilled, (state, action: any) => {
        state.error = null;
        const { isWishlisted, item } = action.payload as { isWishlisted: boolean; item: WishlistItemDto | null };

        // IMPORTANT:
        // When removing, backend returns { isWishlisted: false, item: null }.
        // We must use the original request payload (meta.arg) to compute the signature.
        const arg = action.meta?.arg as { productId: string; designId?: string } | undefined;
        const signatureFromArg = arg?.productId ? signatureFor(arg.productId, arg.designId ?? null) : null;
        const signature = item?.signature ?? signatureFromArg;

        if (isWishlisted && item) {
          const exists = state.items.some((i) => i.signature === item.signature);
          if (exists) {
            state.items = state.items.map((i) => (i.signature === item.signature ? item : i));
          } else {
            state.items = [item, ...state.items];
          }
        } else if (!isWishlisted && signature) {
          state.items = state.items.filter((i) => i.signature !== signature);
        }

        state.count = state.items.length;
      })
      .addCase(toggleWishlistItem.rejected, (state, action: any) => {
        state.error = action.payload || action.error?.message || 'Failed to update wishlist';
      });
  },
});

export const { resetWishlist } = wishlistSlice.actions;

export const selectWishlistItems = (state: any) => state.wishlist?.items || [];
export const selectWishlistCount = (state: any) => state.wishlist?.count || 0;

// 🏪 Memoized selector to prevent unnecessary re-renders
// Returns a stable Set reference when items haven't changed
export const selectWishlistSignatures = createSelector(
  [selectWishlistItems],
  (items: WishlistItemDto[]) => {
    return new Set<string>(items.map((item: WishlistItemDto) => item.signature));
  }
);

export default wishlistSlice.reducer;

